/**
 * Canonical status buckets for "my tasks".
 *
 * - `todo` — task is open (todo block unchecked / status like "To do")
 * - `doing` — in progress ("In progress", "В работе", ...)
 * - `done` — completed (todo checked / status like "Done")
 * - `none` — no status information (database row without a select column)
 */
export type TaskStatus = 'todo' | 'doing' | 'done' | 'none';

export type TaskSource = 'todo' | 'database';

export interface TaskItem {
  /** `${docId}:${blockId}`, unique per task */
  id: string;
  /** Doc (project) the task lives in */
  docId: string;
  /** Block id of the todo list item or the database row */
  blockId: string;
  source: TaskSource;
  /** Plain text title */
  title: string;
  /** Canonical status bucket */
  status: TaskStatus;
  /** Raw select value of the status column, `null` for todo blocks */
  statusLabel: string | null;
  done: boolean;
  /** Due date (ms epoch), from the first date column, if any */
  due: number | null;
  /** Title of the doc the task lives in */
  docTitle: string;
  /** Last update time of the source doc (ms epoch), if known */
  updatedAt: number | null;
}

const DONE_LABELS = new Set([
  'done',
  'completed',
  'complete',
  'closed',
  'finished',
  'выполнено',
  'выполнена',
  'готово',
  'завершено',
  'завершена',
  'закрыто',
]);

const DOING_LABELS = new Set([
  'in progress',
  'in-progress',
  'inprogress',
  'doing',
  'progress',
  'в работе',
  'в процессе',
]);

const TODO_LABELS = new Set([
  'todo',
  'to do',
  'open',
  'backlog',
  'planned',
  'открыта',
  'открыто',
  'открыт',
  'к выполнению',
  'планируется',
  'в плане',
]);

/**
 * Maps a raw status label (select column value) to a canonical bucket.
 * `null`/empty and unknown labels map to `none` — the UI keeps the raw label
 * around (`statusLabel`) and groups by it, so nothing is lost.
 */
export function statusLabelToBucket(label: string | null | undefined): TaskStatus {
  if (!label) return 'none';
  const normalized = label.trim().toLowerCase();
  if (!normalized) return 'none';
  if (DONE_LABELS.has(normalized)) return 'done';
  if (DOING_LABELS.has(normalized)) return 'doing';
  if (TODO_LABELS.has(normalized)) return 'todo';
  return 'none';
}

/** Default sort: by project title, then freshest update first. */
export function sortTaskItems(items: TaskItem[]): TaskItem[] {
  return [...items].sort((a, b) => {
    const docCmp = a.docTitle.localeCompare(b.docTitle);
    if (docCmp !== 0) return docCmp;
    const updatedCmp = (b.updatedAt ?? 0) - (a.updatedAt ?? 0);
    if (updatedCmp !== 0) return updatedCmp;
    return a.title.localeCompare(b.title);
  });
}
